import { Fragment, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box } from '@breathelife/ui-components';
import { PartIdentifierTag } from '@zinnia/form-engine-sdk';

import { Button } from '../../../../Components/Button/Button';
import { IconButton } from '../../../../Components/Controls';
import { ExportButton } from '../../../../Components/ExportData/ExportButton';
import { ExportDataProvider } from '../../../../Components/ExportData/ExportDataProvider';
import { ImportButton } from '../../../../Components/ExportData/ImportButton';
import { ImportDataProvider } from '../../../../Components/ExportData/ImportDataProvider';
import { ImportModal } from '../../../../Components/ExportData/ImportModal';
import { Icon } from '../../../../Components/Icons';
import Typography from '../../../../Components/Typography';
import { useCarrierContext } from '../../../../Hooks';
import { generateQuestionnaireUrl } from '../../../../Navigation/Urls';
import { QuestionnaireEditorContext } from '../ContextProvider/QuestionnaireEditorContextProvider';
import { QuestionnaireVersionDataContext } from '../ContextProvider/QuestionnaireVersionDataContextProvider';
import { LanguageToggle } from './Components/LanguageToggle';
import { PreviewConfigurationModal } from './Components/PreviewConfigurationModal';
import { CreateDraftOrPublish } from './CreateDraftOrPublish';
import { QuestionnaireVersionFlagsSelection } from './QuestionnaireVersionFlagsSelection';

export function QuestionnaireEditorHeader(): ReactElement {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { features, languageSettings } = useCarrierContext();
  const { enabledLanguages } = languageSettings;
  const { selectedLanguage, onSelectedLanguageChange, isMutating, onSelectedPartIdentifierChange } =
    useContext(QuestionnaireEditorContext);
  const { questionnaireVersionData } = useContext(QuestionnaireVersionDataContext);
  const { majorVersion, minorVersion, description, isDraft, isLatestVersion } = questionnaireVersionData || {};
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const isQuestionnaireVersionFlagsEnabled = features.questionnaireVersionFlags.enabled;

  const onExitQuestionnaireEditor = useCallback(() => {
    if (!questionnaireVersionData) return;
    navigate(generateQuestionnaireUrl(questionnaireVersionData?.questionnaireId));
  }, [navigate, questionnaireVersionData]);

  useEffect(() => {
    if (!questionnaireVersionData?.blueprint) {
      return;
    }
    onSelectedPartIdentifierChange({
      tag: PartIdentifierTag.section,
      sectionGroupPartName: 'insuredPeople',
      sectionPartName: questionnaireVersionData.blueprint.sectionBlueprints[0]?.partName ?? '',
    });
    // Disabling dependency array error as this effect should only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <Box display='flex' justifyContent='space-between' m={2}>
        <Box display='flex'>
          <IconButton
            onClick={onExitQuestionnaireEditor}
            icon={<Icon name='leftArrow' />}
            title={t('admin.questionnaireManagement.return')}
          />
          <Box ml={2}>
            <Typography variant='h1'>{t('admin.questionnaireManagement.title')}</Typography>
            <Typography variant='body1'>
              {t('admin.questionnaireManagement.version', {
                majorVersion,
                minorVersion,
                description: description ? description : t('admin.questionnaireManagement.emptyDescription'),
              })}
            </Typography>
          </Box>
        </Box>
        <Box alignSelf='flex-end'>
          <LanguageToggle
            onLanguageChange={onSelectedLanguageChange}
            enabledLanguages={enabledLanguages}
            selectedLanguage={selectedLanguage ?? languageSettings.default}
          />
        </Box>
        <Box display='flex' alignItems='center' justifyContent='flex-end'>
          <Box width='5rem'>
            {isMutating && <Typography variant='small1'>{t('admin.questionnaireManagement.autoSaving')}</Typography>}
          </Box>
          {isQuestionnaireVersionFlagsEnabled && (
            <Box mr={2}>
              <QuestionnaireVersionFlagsSelection
                questionnaireVersionData={questionnaireVersionData}
                isDisabled={isMutating || !isDraft}
              />
            </Box>
          )}
          <Fragment>
            <Box mr={1}>
              <ExportDataProvider blueprint salesDecisionRules flags>
                <ExportButton />
              </ExportDataProvider>
            </Box>
            {isDraft && (
              <ImportDataProvider blueprint salesDecisionRules flags>
                <Box mr={2}>
                  <ImportButton />
                </Box>
                <ImportModal title={t('admin.importData.blueprintsAndSalesDecisionRules')} />
              </ImportDataProvider>
            )}
          </Fragment>
          <Fragment>
            {features.questionnairePreview.enabled && (
              <Box mr={1}>
                <Button
                  color='inherit'
                  variant='contained'
                  onClick={() => setIsPreviewModalOpen(true)}
                  disabled={false}
                >
                  {t('admin.questionnaireManagement.preview')}
                </Button>
              </Box>
            )}
            {isPreviewModalOpen && <PreviewConfigurationModal isOpen onClose={() => setIsPreviewModalOpen(false)} />}
          </Fragment>
          <Box>
            <CreateDraftOrPublish
              disabled={isMutating}
              isDraft={Boolean(isDraft)}
              description={description ?? ''}
              isLatestVersion={Boolean(isLatestVersion)}
            />
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
}
